export const defaultProgramDetails = [
  {
    id: "healthcare",
    heroImage: "/assets/agm/polyclinic/polyclinic-medical-team.jpg",
    heroPosition: "center 44%",
    location: "Harihar Ashram, Kankhal, Haridwar",
    deck:
      "Swami Avdheshanand Polyclinic brings free medical consultation, medicine access and preventive care closer to families who need dependable healthcare support.",
    lead:
      "The polyclinic is designed as healthcare seva: disciplined, dignified and practical. Doctors, medical staff, volunteers and close medical partners such as Medanta work around the needs of patients who may delay treatment because consultation, medicines or basic guidance are difficult to access.",
    stats: [
      { value: "Free", label: "Consultation seva", note: "Medical support offered with dignity and care." },
      { value: "Medicine", label: "Access support", note: "Essential medicines arranged for patients and camps." },
      { value: "Doctor-led", label: "Care model", note: "Medical teams guide consultation and follow-up." },
      { value: "Medanta", label: "Care partner", note: "Specialist support strengthens camp and mobile-care reach." },
    ],
    sections: [
      {
        eyebrow: "Why it matters",
        title: "Healthcare becomes social upliftment when timely care reaches the family first.",
        body:
          "For many households, a missed consultation becomes a missed school day, a lost workday or a bigger medical burden later. The polyclinic helps reduce that distance between need and care through accessible consultation, medicines and trusted guidance.",
      },
      {
        eyebrow: "How the care works",
        title: "A practical flow from registration to consultation, medicines, partner support and guidance.",
        body:
          "Patients are received, guided to appropriate medical support, assisted with prescribed medicines where possible and encouraged toward preventive habits. With Medanta working closely with the polyclinic, AGM can connect patients to stronger clinical support while keeping the emphasis on consistent, respectful care.",
      },
      {
        eyebrow: "Vedant in action",
        title: "Seva here means seeing the patient as family, not as a case number.",
        body:
          "Vedantic wisdom teaches the dignity of every life. In the polyclinic, that idea becomes patient listening, clean process, medicine support and the humility to serve without discrimination.",
      },
    ],
    pathways: [
      "General consultation and first-line care for patients.",
      "Medicine support through pharmacy and camp arrangements.",
      "Close coordination with Medanta teams for mobile medical support and specialty reach.",
      "Specialty attention such as dental, eye-care and preventive screening as available.",
      "Health awareness so families can act earlier, not only during emergencies.",
    ],
    donorUses: [
      "Sponsor medicine kits and diagnostic support.",
      "Support visiting doctors, nurses and paramedical teams.",
      "Strengthen Medanta-supported mobile medical camp logistics.",
      "Help organize health camps for nearby communities.",
      "Strengthen patient records, follow-up and outreach communication.",
    ],
    gallery: [
      {
        src: "/assets/agm/polyclinic/polyclinic-team-banner.jpg",
        alt: "Swami Avdheshanand Polyclinic medical team under the clinic banner",
        caption: "Medical team presence at the free healthcare service.",
      },
      {
        src: "/assets/agm/polyclinic/polyclinic-medical-team.jpg",
        alt: "Pujya Swami ji with doctors, staff and medicine supplies",
        caption: "Care teams and medicine resources brought together for patient support.",
      },
      {
        src: "/assets/agm/polyclinic/polyclinic-medanta-mobile-unit.jpg",
        alt: "Pujya Swami ji with Medanta doctors and mobile medical unit at Swami Avdheshanand Polyclinic",
        caption: "Medanta works closely with Swami Avdheshanand Polyclinic, strengthening mobile medical support and clinical reach.",
      },
      {
        src: "/assets/agm/polyclinic/polyclinic-inauguration.jpg",
        alt: "Swami ji with dignitaries and doctors at the polyclinic",
        caption: "Community leadership and medical professionals supporting the initiative.",
      },
      {
        src: "/assets/agm/polyclinic/polyclinic-signage.jpg",
        alt: "Swami Avdheshanand Polyclinic signage",
        caption: "The free medical service identified for local patients and visitors.",
      },
      {
        src: "/assets/agm/polyclinic/polyclinic-group-vertical.jpg",
        alt: "Group photograph of Swami ji and medical team at the polyclinic",
        caption: "Doctors, staff and volunteers standing together for healthcare seva.",
      },
    ],
  },
  {
    id: "future-skilling",
    heroImage: "/assets/agm/ifs-students.jpg",
    heroPosition: "center",
    location: "Institute of Future Skilling",
    deck:
      "IFS gives young people access to modern tools so education can become employability, self-trust and a real path into the future.",
    lead:
      "The Institute of Future Skilling focuses on practical learning: coding, graphic design, video editing, Tally, digital workflows, communication and job readiness. The goal is not only training, but confidence for students who need a fair chance in the modern economy.",
    stats: [
      { value: "2,000", label: "Youth per year", note: "Annual skilling ambition across cohorts." },
      { value: "Digital", label: "Career tools", note: "Coding, design, editing, accounting and productivity." },
      { value: "Inclusive", label: "Access", note: "Girls and underserved learners included." },
      { value: "Job-ready", label: "Outcome focus", note: "Portfolio, interview and workplace readiness." },
    ],
    sections: [
      {
        eyebrow: "Why it matters",
        title: "Talent is everywhere, but access to tools is not.",
        body:
          "IFS closes the distance between aspiration and opportunity by giving learners the software exposure, mentorship and practice environment needed to compete with confidence.",
      },
      {
        eyebrow: "Learning model",
        title: "Skills are taught through doing, not only through lectures.",
        body:
          "Students work on practical assignments, creative projects, accounting workflows and digital tasks that can become portfolio proof when they apply for jobs or freelance work.",
      },
      {
        eyebrow: "Vedant in action",
        title: "Education becomes seva when it restores self-belief.",
        body:
          "Vedant points to the potential already present in every person. IFS turns that insight into training that helps young people recognize capability, discipline and dignity within themselves.",
      },
    ],
    pathways: [
      "Digital literacy and software confidence.",
      "Coding, design, editing and accounting tool exposure.",
      "Portfolio building, interview readiness and workplace conduct.",
      "Mentorship for students entering first jobs or internships.",
    ],
    donorUses: [
      "Sponsor student seats and course material.",
      "Upgrade computers, software and classroom infrastructure.",
      "Fund expert trainers and mentorship sessions.",
      "Support placement readiness and alumni tracking.",
    ],
    gallery: [
      { src: "/assets/agm/ifs-students.jpg", alt: "IFS students in training", caption: "Young learners preparing for digital careers." },
      { src: "/assets/agm/ifs-lab.jpg", alt: "Institute of Future Skilling lab", caption: "A practical lab environment for future skills." },
      { src: "/assets/agm/web/ifsl-lab-0.webp", alt: "IFS computer classroom", caption: "Hands-on computer learning with structured guidance." },
      { src: "/assets/agm/web/ifsl-lab-1.webp", alt: "Students learning in IFS lab", caption: "Students practicing tools that improve employability." },
      { src: "/assets/agm/web/ifsl-lab-3.webp", alt: "IFS classroom training", caption: "Classroom energy built around real practice." },
      { src: "/assets/agm/web/ifsl-lab-5.webp", alt: "IFS lab session", caption: "Digital training designed for confidence and livelihood." },
    ],
  },
  {
    id: "school",
    heroImage: "/assets/agm/school/school-cultural-stage.jpg",
    heroPosition: "center 48%",
    location: "Champawat, Uttarakhand",
    deck:
      "Avdheshanand Public School brings classroom learning, cultural confidence, discipline and values-led education together for children in Champawat.",
    lead:
      "The school is AGM's long-term education commitment in Champawat. Children are supported not only with academics, but with confidence, conduct, stage exposure, cultural grounding and the possibility of a larger life.",
    stats: [
      { value: "Values", label: "Education", note: "Learning shaped by discipline, respect and self-belief." },
      { value: "Stage", label: "Confidence", note: "Students gain expression through cultural programs and public performance." },
      { value: "Modern", label: "Aspirations", note: "Digital exposure and stronger classrooms remain part of the growth vision." },
      { value: "Champawat", label: "Field base", note: "Rural education upliftment rooted in community trust." },
    ],
    sections: [
      {
        eyebrow: "Why it matters",
        title: "A good school can change the emotional climate of an entire family.",
        body:
          "When a child gains confidence, parents begin to imagine new futures. Avdheshanand Public School carries that responsibility with academic learning, cultural formation and the steady discipline that helps children stand taller in life.",
      },
      {
        eyebrow: "Learning environment",
        title: "Education must prepare children for livelihood, character and public confidence.",
        body:
          "The school vision includes stronger classrooms, computer access, disciplined routines, student confidence and exposure to ideas that help children participate in a changing world without losing cultural grounding.",
      },
      {
        eyebrow: "Culture and expression",
        title: "The stage becomes a classroom for confidence, memory, teamwork and pride.",
        body:
          "Students participate in cultural performances, Vedic chanting, national programs and group presentations. These moments build public speaking, teamwork, reverence, courage and the feeling that their voice matters.",
      },
      {
        eyebrow: "Vedant in action",
        title: "Every child carries dignity before any achievement.",
        body:
          "Vedantic service begins with respect for the inner worth of each child. The school environment helps children grow with academics, humility, discipline and human sensitivity moving together.",
      },
    ],
    pathways: [
      "Classroom strengthening and teaching support.",
      "Cultural programs, Vedic chanting, national days and stage confidence.",
      "Computer lab, smart-classroom and modern learning development.",
      "Student confidence, discipline, teamwork and cultural grounding.",
      "Parent-community participation in education upliftment.",
    ],
    donorUses: [
      "Sponsor books, uniforms, learning material and student essentials.",
      "Support smart classrooms, computer lab infrastructure and digital exposure.",
      "Fund cultural programs, stage equipment, sports and co-curricular growth.",
      "Help improve libraries, teacher development and student confidence programs.",
    ],
    gallery: [
      {
        src: "/assets/agm/school/school-cultural-stage.jpg",
        alt: "Pujya Swami ji with Avdheshanand Public School students during a cultural program",
        caption: "Students learn confidence, tradition and expression through cultural stage programs.",
      },
      {
        src: "/assets/agm/school/school-students-staff.jpg",
        alt: "Pujya Swami ji with Avdheshanand Public School students and staff",
        caption: "Students and staff together reflect the school's values-led education environment.",
      },
      {
        src: "/assets/agm/school/school-veda-chanting.jpg",
        alt: "Students in traditional robes chanting on stage",
        caption: "Vedic chanting and traditional learning build discipline, memory and reverence.",
      },
      {
        src: "/assets/agm/school/school-national-tribute.jpg",
        alt: "Students holding Indian flags during a school performance",
        caption: "National and cultural programs help students build pride, teamwork and stage confidence.",
      },
      {
        src: "/assets/agm/school/school-vedpathshala-students.jpg",
        alt: "Students seated in traditional robes during a school program",
        caption: "A values-rooted environment where education includes conduct, devotion and self-respect.",
      },
      {
        src: "/assets/agm/champawat-school.jpg",
        alt: "Swami Avdheshanand Public School Champawat",
        caption: "A school environment focused on rural education upliftment.",
      },
    ],
  },
  {
    id: "mahakumbh",
    heroImage: "/assets/agm/mahakumbh-medical.jpg",
    heroPosition: "center",
    location: "Mahakumbh Seva Camps",
    deck:
      "During Mahakumbh, AGM organizes food, medical care, shelter, transport and relief support for pilgrims moving through an immense spiritual gathering.",
    lead:
      "Mahakumbh seva requires scale, discipline and compassion. AGM's role is to support pilgrims through practical systems: medical camps, meals, shelter, blankets, transportation and on-ground coordination.",
    stats: [
      { value: "60,000+", label: "Meals served", note: "Food distribution for pilgrims and volunteers." },
      { value: "31,669", label: "Pilgrims treated", note: "Medical care delivered through camp support." },
      { value: "35,000+", label: "Sheltered", note: "Support for pilgrims needing rest and safety." },
      { value: "4,000", label: "Blankets", note: "Warmth and relief during peak crowd movement." },
    ],
    sections: [
      {
        eyebrow: "Why it matters",
        title: "A sacred gathering becomes safer when seva is organized with precision.",
        body:
          "Pilgrims arrive with devotion, but also face fatigue, weather, crowd movement and medical needs. AGM's support helps transform devotion into a safer human experience.",
      },
      {
        eyebrow: "Field model",
        title: "Relief is delivered through camps, supplies, volunteers and coordination.",
        body:
          "Food, healthcare, shelter, blankets and transport support are arranged in a way that keeps attention on the pilgrim's dignity and immediate need.",
      },
      {
        eyebrow: "Vedant in action",
        title: "The pilgrim is not a crowd. Each person is a life to be served.",
        body:
          "Vedant turns mass service into personal responsibility. Every meal, blanket and medical consultation becomes an offering made without ego.",
      },
    ],
    pathways: [
      "Medical camps and emergency health response.",
      "Food distribution and safe hydration support.",
      "Shelter, blankets and rest areas for pilgrims.",
      "Transport assistance and volunteer coordination.",
    ],
    donorUses: [
      "Fund meals, drinking water and relief material.",
      "Support medical camps, medicines and first-aid supplies.",
      "Sponsor blankets, tents and shelter infrastructure.",
      "Help train and equip volunteer teams.",
    ],
    gallery: [
      { src: "/assets/agm/mahakumbh-medical.jpg", alt: "Mahakumbh medical seva", caption: "Medical support for pilgrims during Mahakumbh." },
      { src: "/assets/agm/mahakumbh-satsang.jpg", alt: "Mahakumbh satsang and gathering", caption: "Spiritual gathering supported by organized seva." },
      { src: "/assets/agm/mahakumbh-seva.jpg", alt: "Mahakumbh relief seva", caption: "Relief work for pilgrims at human scale." },
      { src: "/assets/agm/polyclinic-consultation.jpg", alt: "Healthcare support", caption: "Healthcare is a central part of AGM's public service." },
    ],
  },
  {
    id: "mass-wedding",
    heroImage: "/assets/agm/mass-wedding-zora.jpg",
    heroPosition: "center",
    location: "The Zora Convention Centre, New Delhi",
    deck:
      "Shagun Vivah Utsav 2026 supported 51 couples with blessings, financial help, household essentials and a public celebration of dignity.",
    lead:
      "Held on March 31, 2026 in collaboration with Ladli Foundation Trust and partners, the initiative helped couples begin married life with community honor instead of financial pressure.",
    stats: [
      { value: "51", label: "Couples", note: "Supported through the Shagun Vivah Utsav." },
      { value: "₹1L", label: "Cheque support", note: "Financial support for each couple as shared by the mission." },
      { value: "Essentials", label: "Household start", note: "Dahej-equivalent essentials arranged with dignity." },
      { value: "Public", label: "Blessings", note: "Dignitaries, officers and families joined the ceremony." },
    ],
    sections: [
      {
        eyebrow: "Why it matters",
        title: "Dignity is protected when marriage does not become a crushing burden.",
        body:
          "For families with limited means, a wedding can become debt, stress and social anxiety. This initiative reframed marriage support as dignity, community blessing and practical household stability.",
      },
      {
        eyebrow: "Event model",
        title: "The program combined ceremony, financial assistance and household support.",
        body:
          "Couples received blessings, financial cheques, household essentials, ceremonial arrangements and a shared meal for families and guests in a setting that respected their joy.",
      },
      {
        eyebrow: "Vedant in action",
        title: "Seva protects dignity at life's most important thresholds.",
        body:
          "Vedantic service asks society to stand with people during vulnerable transitions. A marriage supported with dignity becomes a statement that community care still matters.",
      },
    ],
    pathways: [
      "Financial support for newly married couples.",
      "Household essentials and dignity-centered arrangements.",
      "Community participation with families, officers and dignitaries.",
      "Public storytelling to inspire more social participation.",
    ],
    donorUses: [
      "Sponsor future dignity-centered marriage support.",
      "Fund household essentials and financial assistance.",
      "Support event logistics, meals and family hospitality.",
      "Help document beneficiary stories responsibly.",
    ],
    gallery: [
      { src: "/assets/agm/mass-wedding-zora.jpg", alt: "Shagun Vivah Utsav at Zora Convention Centre", caption: "A public celebration of dignity for 51 couples." },
      { src: "/assets/agm/mass-wedding-ceremony.jpg", alt: "Mass wedding ceremony", caption: "Ceremony, blessings and family participation." },
      { src: "/assets/agm/mass-wedding-dignitaries.jpg", alt: "Dignitaries at Shagun Vivah Utsav", caption: "Public leadership standing with the couples." },
      { src: "/assets/agm/mass-wedding-shagun-hero.png", alt: "Shagun Vivah Utsav couple support", caption: "Household support and blessings for a stable beginning." },
    ],
  },
  {
    id: "vedant",
    heroImage: "/assets/agm/swamiji-vedant.jpg",
    heroPosition: "center",
    location: "Vedantic wisdom in public life",
    deck:
      "Vedant gives AGM its inner compass: oneness must become compassion, and compassion must become disciplined social action.",
    lead:
      "AGM's service work is not separate from spiritual wisdom. Healthcare, education, skilling, relief and dignity initiatives are practical expressions of the Vedantic insight that every life deserves respect.",
    stats: [
      { value: "Seva", label: "Without ego", note: "Action offered as responsibility." },
      { value: "Dignity", label: "Without bias", note: "Each person served as family." },
      { value: "Wisdom", label: "In action", note: "Vedant translated into public good." },
      { value: "Society", label: "As sadhana", note: "Service becomes inner discipline." },
    ],
    sections: [
      {
        eyebrow: "Why it matters",
        title: "Wisdom remains incomplete if it does not change how society treats people.",
        body:
          "Vedant is not only a philosophy to admire. It is a discipline that changes action: how we feed, teach, heal, listen and stand with people in need.",
      },
      {
        eyebrow: "AGM model",
        title: "Every program is a way to practice oneness in public life.",
        body:
          "Healthcare protects the body, education opens possibility, skilling builds livelihood, relief protects pilgrims and dignity programs support families. Together, they turn spiritual insight into social structure.",
      },
      {
        eyebrow: "Adi Shankaracharya lineage",
        title: "The wisdom of oneness becomes meaningful when it dissolves indifference.",
        body:
          "Adi Shankaracharya's Advaita points toward the unity of existence. AGM brings that spirit into seva by treating social responsibility as a natural extension of spiritual understanding.",
      },
    ],
    pathways: [
      "Public learning around Vedantic wisdom and seva.",
      "Content that connects ancient teachings to everyday responsibility.",
      "Programs where spiritual values become visible outcomes.",
      "A bridge for global supporters who want service with depth.",
    ],
    donorUses: [
      "Support educational content and public Vedant programs.",
      "Help document service stories through a Vedantic lens.",
      "Fund youth learning, satsang access and community outreach.",
      "Strengthen the bridge between spiritual learning and seva.",
    ],
    gallery: [
      { src: "/assets/agm/swamiji-vedant.jpg", alt: "Swami ji teaching Vedant", caption: "Vedantic wisdom gives AGM its inner discipline." },
      { src: "/assets/agm/vedant-reference/adi-shankara-dwarka-hero.jpg", alt: "Adi Shankaracharya reference", caption: "Adi Shankaracharya remains central to the Advaita tradition." },
      { src: "/assets/agm/vedant-reference/omkareshwar-narmada-wide.jpg", alt: "Omkareshwar Narmada", caption: "Sacred geography and wisdom traditions guiding public life." },
      { src: "/assets/agm/swamiji-guiding-inspiration.jpg", alt: "Pujya Swami ji", caption: "Swami ji's guidance turns wisdom toward social upliftment." },
    ],
  },
];
